import { useCallback, useMemo, useState } from 'react';
import useSWRInfinite from 'swr/infinite';
import { useTranslation } from 'react-i18next';
import { CarIcon, UserIcon } from '@phosphor-icons/react';
import {
    Combobox,
    type ComboboxOption,
    type ComboboxOptionRenderMeta,
} from '@/components/Combobox/Combobox';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine/engine-service';
import type { EntityKind } from '@/utils/detection-classes';
import { cn } from '@/lib/utils';
import { EntityThumbnailWithPreview } from './EntityThumbnailWithPreview';

const PAGE_SIZE = 20;

interface LinkEntityPickerProps {
    entityKind: EntityKind;
    sourceEntityId: string;
    onLink: (targetEntityId: string) => void;
    onClose?: () => void;
    title?: string;
}

interface PickerRow {
    id: string;
    label: string;
    subLabel?: string | null;
    imageUrl?: string | null;
}

interface PageResult {
    items: PickerRow[];
    total: number;
}

type PageKey = readonly [
    'link-entity-picker',
    EntityKind,
    string,
    string,
    number,
];

export function LinkEntityPicker({
    entityKind,
    sourceEntityId,
    onLink,
    onClose,
    title,
}: LinkEntityPickerProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const [search, setSearch] = useState('');

    const excludeIds = sourceEntityId ? [sourceEntityId] : [];

    const fetchPage = useCallback(
        async ([
            ,
            ,
            currentSearch,
            excludeIdsJoined,
            page,
        ]: PageKey): Promise<PageResult> => {
            const excludeList = excludeIdsJoined
                ? excludeIdsJoined.split(',')
                : [];
            if (entityKind === 'person') {
                const response = await EngineService.searchPersonMergeTargets({
                    search: currentSearch,
                    excludePersonIds: excludeList,
                    page,
                    size: PAGE_SIZE,
                });
                return {
                    items: response.items.map((item) => ({
                        id: item.personId,
                        label:
                            item.name ??
                            t('eventView.linkEntity.unknownPerson'),
                        imageUrl: item.imageUrl,
                    })),
                    total: response.page.totalItems,
                };
            }
            const response = await EngineService.searchVehicleMergeTargets({
                search: currentSearch,
                excludeVehicleIds: excludeList,
                page,
                size: PAGE_SIZE,
            });
            return {
                items: response.items.map((item) => ({
                    id: item.vehicleId,
                    label: item.name ?? item.licensePlate,
                    subLabel: item.name ? item.licensePlate : null,
                    imageUrl: item.imageUrl,
                })),
                total: response.page.totalItems,
            };
        },
        [entityKind, t],
    );

    const { data, isValidating, size, setSize } = useSWRInfinite<
        PageResult,
        unknown,
        (
            pageIndex: number,
            previousPageData: PageResult | null,
        ) => PageKey | null
    >(
        (pageIndex, previousPageData) => {
            if (previousPageData && previousPageData.items.length === 0) {
                return null;
            }
            return [
                'link-entity-picker',
                entityKind,
                search,
                excludeIds.join(','),
                pageIndex + 1,
            ] as const;
        },
        fetchPage,
        { revalidateFirstPage: false, revalidateOnMount: true },
    );

    const rows = useMemo(
        () => data?.flatMap((page) => page.items) ?? [],
        [data],
    );

    const total = data?.[0]?.total ?? 0;
    const hasMore = rows.length < total;

    const options: ComboboxOption[] = useMemo(
        () => rows.map((row) => ({ value: row.id, label: row.label })),
        [rows],
    );

    const rowsById = useMemo(
        () => Object.fromEntries(rows.map((row) => [row.id, row])),
        [rows],
    );

    const handleSearch = (query: string) => {
        setSearch(query);
        void setSize(1);
    };

    const handleLoadMore = () => {
        if (isValidating || !hasMore) return;
        void setSize(size + 1);
    };

    const renderOption = useCallback(
        (option: ComboboxOption, { isSelected }: ComboboxOptionRenderMeta) => {
            const row = rowsById[option.value];
            return (
                <div
                    className={cn(
                        'flex w-full items-center gap-3 rounded-md px-2 py-2',
                        isSelected && 'bg-grey-500',
                    )}
                >
                    <EntityThumbnailWithPreview
                        imageUrl={row?.imageUrl}
                        name={option.label}
                        size="sm"
                        fallbackIcon={
                            entityKind === 'person' ? (
                                <UserIcon className="text-grey-400" size={20} />
                            ) : (
                                <CarIcon className="text-grey-400" size={20} />
                            )
                        }
                    />
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-white-100">
                            {option.label}
                        </p>
                        {row?.subLabel && (
                            <p className="truncate text-xs text-grey-300">
                                {row.subLabel}
                            </p>
                        )}
                    </div>
                </div>
            );
        },
        [rowsById, entityKind],
    );

    const handleToggle = useCallback(
        (entityId: string) => {
            onLink(entityId);
            onClose?.();
        },
        [onLink, onClose],
    );

    return (
        <div className="w-72">
            <Combobox
                options={options}
                selected={[]}
                onToggle={handleToggle}
                onSearch={handleSearch}
                onLoadMore={handleLoadMore}
                hasMore={hasMore}
                isLoading={isValidating}
                searchPlaceholder={t('eventView.linkEntity.search')}
                emptyText={t('eventView.linkEntity.empty')}
                renderOption={renderOption}
                title={title ?? t('eventView.linkEntity.title')}
                autoFocus
            />
        </div>
    );
}
